import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderService, type Order, type OrderListResponse, orderStatusConfig, type OrderStatus } from '../../services';
import { useAuthStore } from '../../stores/authStore';
import toast from 'react-hot-toast';
import { Download, Eye, Filter, RefreshCw } from 'lucide-react';

const OrdersManagementPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<OrderListResponse | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [status, setStatus] = useState<OrderStatus | ''>('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
      navigate('/dashboard', { replace: true });
      return;
    }
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const res = await orderService.getAllOrders(page, pageSize, status || undefined);
      setList(res);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('خطا در دریافت لیست سفارشات');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = useMemo(() => list?.total_pages ?? 1, [list]);

  const updateStatus = async (id: string, newStatus: OrderStatus) => {
    setUpdatingId(id);
    try {
      await orderService.updateStatus(id, newStatus);
      setList((prev: OrderListResponse | null) => {
        if (!prev) return prev;
        return {
          ...prev,
          orders: prev.orders.map((o: Order) => (o.id === id ? { ...o, status: newStatus } : o)),
        };
      });
      toast.success('وضعیت سفارش با موفقیت تغییر کرد');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('خطا در تغییر وضعیت سفارش');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await orderService.exportOrders(status || undefined);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('فایل خروجی با موفقیت دانلود شد');
    } catch (error) {
      console.error('Error exporting orders:', error);
      toast.error('خطا در دریافت فایل خروجی');
    } finally {
      setExporting(false);
    }
  };

  const formatPrice = (price: string | number) => {
    return new Intl.NumberFormat('fa-IR').format(typeof price === 'string' ? parseFloat(price) : price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-4 lg:p-8 container mx-auto max-w-7xl">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">مدیریت سفارشات</h1>
          <p className="text-gray-500 text-sm">مشاهده همه سفارشات و تغییر وضعیت</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Filter className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select 
              className="input-field h-10 pr-9 pl-4 text-sm min-w-[160px]" 
              value={status} 
              onChange={(e) => setStatus(e.target.value as OrderStatus | '')}
            >
              <option value="">همه وضعیت‌ها</option>
              {Object.keys(orderStatusConfig).map((s) => (
                <option key={s} value={s}>
                  {orderStatusConfig[s as OrderStatus].label}
                </option>
              ))}
            </select>
          </div>
          
          <button 
            className="btn-secondary h-10 px-4 text-sm flex items-center gap-2" 
            onClick={() => { setPage(1); loadOrders(); }}
            title="بروزرسانی لیست"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">بروزرسانی</span>
          </button>
          
          <button 
            className="btn-primary h-10 px-4 text-sm flex items-center gap-2"
            onClick={handleExport}
            disabled={exporting}
            title="خروجی اکسل"
          >
            {exporting ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">خروجی اکسل</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p>در حال بارگزاری سفارشات...</p>
          </div>
        ) : !list?.orders?.length ? (
          <div className="p-12 text-center text-gray-500">
            <p>سفارشی یافت نشد.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-right">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-gray-600">
                  <th className="p-4 font-medium">شماره سفارش</th>
                  <th className="p-4 font-medium">تاریخ ثبت</th>
                  <th className="p-4 font-medium">شناسه کاربر</th>
                  <th className="p-4 font-medium">تعداد اقلام</th>
                  <th className="p-4 font-medium">مبلغ کل (تومان)</th>
                  <th className="p-4 font-medium">وضعیت</th>
                  <th className="p-4 font-medium">تغییر وضعیت</th>
                  <th className="p-4 font-medium text-center">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {list.orders.map((o: Order) => (
                  <tr key={o.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="p-4 font-mono text-xs text-gray-500">
                      {o.id.split('-')[0]}...
                    </td>
                    <td className="p-4 text-gray-600">
                      {formatDate(o.created_at)}
                    </td>
                    <td className="p-4 text-gray-900" title={o.user_id}>
                      {o.user_phone_number ? (
                        <span className="font-medium" dir="ltr">{o.user_phone_number}</span>
                      ) : (
                        <span className="font-mono text-xs text-gray-500">{o.user_id.split('-')[0]}...</span>
                      )}
                    </td>
                    <td className="p-4 text-gray-900">
                      {o.items?.length ?? 0}
                    </td>
                    <td className="p-4 font-bold text-gray-900">
                      {formatPrice(o.total_amount)}
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${orderStatusConfig[o.status].bgColor} ${orderStatusConfig[o.status].color}`}>
                        {orderStatusConfig[o.status].label}
                      </span>
                    </td>
                    <td className="p-4">
                      <select
                        className="input-field py-1 px-2 text-xs w-auto bg-white"
                        value={o.status}
                        onChange={(e) => updateStatus(o.id, e.target.value as OrderStatus)}
                        disabled={updatingId === o.id}
                      >
                        {Object.keys(orderStatusConfig).map((s) => (
                          <option key={s} value={s}>
                            {orderStatusConfig[s as OrderStatus].label}
                          </option>
                        ))}
                      </select>
                      {updatingId === o.id && (
                        <span className="mr-2 text-xs text-primary">در حال ثبت...</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => setSelectedOrder(o)}
                        className="p-2 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                        title="مشاهده جزئیات"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-8">
          <button 
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" 
            onClick={() => setPage((p) => Math.max(1, p - 1))} 
            disabled={page <= 1}
          >
            قبلی
          </button>
          <span className="text-sm text-gray-600 px-2">صفحه {page} از {totalPages}</span>
          <button 
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" 
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))} 
            disabled={page >= totalPages}
          >
            بعدی
          </button>
        </div>
      )}

      {/* Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedOrder(null)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-xl font-bold text-gray-900">جزئیات سفارش</h2>
                <p className="text-sm text-gray-500 mt-1">
                  شماره سفارش: <span className="font-mono">{selectedOrder.id}</span>
                </p>
              </div>
              <button 
                onClick={() => setSelectedOrder(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-xl space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500 text-sm">کاربر:</span>
                    <span className="font-medium text-sm" dir="ltr">
                      {selectedOrder.user_phone_number || <span className="font-mono text-xs">{selectedOrder.user_id}</span>}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 text-sm">تاریخ ثبت:</span>
                    <span className="text-sm">{formatDate(selectedOrder.created_at)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">وضعیت:</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${orderStatusConfig[selectedOrder.status].bgColor} ${orderStatusConfig[selectedOrder.status].color}`}>
                      {orderStatusConfig[selectedOrder.status].label}
                    </span>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-xl space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500 text-sm">مبلغ کل:</span>
                    <span className="font-bold text-gray-900">{formatPrice(selectedOrder.total_amount)} تومان</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 text-sm">قیمت طلا در لحظه:</span>
                    <span className="text-gray-900">{formatPrice(selectedOrder.gold_price_at_time)} تومان</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 text-sm">تعداد اقلام:</span>
                    <span className="text-gray-900">{selectedOrder.items?.length || 0} عدد</span>
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <div>
                <h3 className="font-bold text-gray-900 mb-4">اقلام سفارش</h3>
                <div className="border border-gray-100 rounded-xl overflow-hidden">
                  <table className="w-full text-sm text-right">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="p-3 font-medium">#</th>
                        <th className="p-3 font-medium">تصویر</th>
                        <th className="p-3 font-medium">محصول</th>
                        <th className="p-3 font-medium">تعداد</th>
                        <th className="p-3 font-medium">قیمت واحد</th>
                        <th className="p-3 font-medium">قیمت کل</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {selectedOrder.items?.map((item, index) => (
                        <tr key={index}>
                          <td className="p-3 text-gray-500">{index + 1}</td>
                          <td className="p-3">
                            <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden">
                              {item.product?.image_url ? (
                                <img src={item.product.image_url} alt={item.product.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-300">
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="font-medium text-gray-900">{item.product?.name || 'محصول حذف شده'}</div>
                            <div className="text-xs text-gray-500 font-mono mt-0.5">{item.product_id.split('-')[0]}...</div>
                          </td>
                          <td className="p-3 text-gray-900">{item.quantity}</td>
                          <td className="p-3 text-gray-600">{formatPrice(item.price)}</td>
                          <td className="p-3 font-bold text-gray-900">{formatPrice(parseInt(item.price) * item.quantity)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex justify-end">
              <button 
                onClick={() => setSelectedOrder(null)}
                className="btn-secondary px-6"
              >
                بستن
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersManagementPage;
